import { useState, useMemo } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs, { Dayjs } from 'dayjs'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { useIntl } from 'react-intl'
import SideModalWrapper from '../../../Assets/modals/SideModalWrapper'
import { messages } from '../../messages'
import { useGetLocationsQuery } from '../../../../app/redux-fetch/apiCoreQuery'
import { useGetUnassignedConsignmentsQuery } from '../../../../app/redux-fetch/apiOrders'
import { Route, LoadMetadata, DestinationActionTypeId } from '../plannerTypes'
import { UnassignedConsignment, UnassignedWaypoint, UnassignedTransitDetail } from '../../types'
import ConsignmentContentsSidePanel from '../../components/ConsignmentContentsSidePanel'
import ConsignmentServicesSidePanel from '../../components/ConsignmentServicesSidePanel'

type CustomerRequestRow = {
  loadId: number
  shipmentBookingId: number
  trackingNumber: string
  type: string
  quantity: number
  tempControlled: string
  shipFrom: string
  waypointCount: number
  deliverTo: string
  returnLocation: string
  customer: string
  waypoints: UnassignedWaypoint[]
}

type ServiceConfig = {
  loadId: number
  actionTypeId: number
  clearanceRequired: boolean
  selectedServiceIds: number[]
}

type Service = {
  id: number
  name: string
  enabled: boolean
  customerSelectable: boolean
  loadTypes?: { loadTypeId: number; loadType?: { id: number; name: string } }[]
}

type AddDestinationSidePanelProps = {
  open: boolean
  onClose: () => void
  onAdd: (
    locationId: number,
    locationName: string | undefined,
    address: string | undefined,
    scheduledDate: string,
    loadActions: { loadId: number; destinationActionTypeId: number }[],
    loadServices: { loadId: number; serviceId: number }[]
  ) => void
  route: Route
  loadMetadataById: Record<number, LoadMetadata>
  tenantId: number
  services: Service[]
  minDate?: Dayjs
}

const AddDestinationSidePanel = ({
  open,
  onClose,
  onAdd,
  route,
  loadMetadataById,
  tenantId,
  services,
  minDate,
}: AddDestinationSidePanelProps) => {
  const { formatMessage } = useIntl()
  const [selectedLocationId, setSelectedLocationId] = useState<number | ''>('')
  const [scheduledDate, setScheduledDate] = useState<Dayjs | null>(null)
  const [selectedLoadIds, setSelectedLoadIds] = useState<number[]>([])
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())
  const [serviceConfigs, setServiceConfigs] = useState<Record<number, ServiceConfig>>({})

  // State for Contents side panel (opened from Quantity column)
  const [contentsSidePanelOpen, setContentsSidePanelOpen] = useState(false)
  const [contentsConsignment, setContentsConsignment] = useState<UnassignedConsignment | null>(null)

  // State for Services side panel (opened from Services column in waypoint details)
  const [servicesSidePanelOpen, setServicesSidePanelOpen] = useState(false)
  const [servicesConsignment, setServicesConsignment] = useState<UnassignedConsignment | null>(null)
  const [servicesWaypoint, setServicesWaypoint] = useState<UnassignedWaypoint | null>(null)

  const { data: locations = [] } = useGetLocationsQuery(
    { tenantId },
    { skip: !open || !tenantId }
  )

  // Fetch consignment data to get waypoint and transit details
  const { data: consignments = [] } = useGetUnassignedConsignmentsQuery(
    { tenantId },
    { skip: !open || !tenantId }
  )

  // Build a map of shipmentBookingId to consignment for quick lookup
  const consignmentByShipmentBookingId = useMemo(() => {
    const map: Record<number, UnassignedConsignment> = {}
    consignments.forEach((c) => {
      map[c.shipmentBookingId] = c
    })
    return map
  }, [consignments])

  const customerRequestRows: CustomerRequestRow[] = useMemo(() => {
    // Use loadIds from the route to show only loads that belong to this specific route
    // This allows users to add any load from this route to a new destination
    return route.loadIds.map((loadId) => {
      const metadata = loadMetadataById[loadId]
      const consignment = metadata?.shipmentBookingId 
        ? consignmentByShipmentBookingId[metadata.shipmentBookingId] 
        : undefined
      
      return {
        loadId,
        shipmentBookingId: metadata?.shipmentBookingId || 0,
        trackingNumber: metadata?.trackingNumber || '-',
        type: metadata?.loadTypeName || '-',
        quantity: metadata?.quantity || 0,
        tempControlled: consignment?.temperatureControlledCount 
          ? `${consignment.temperatureControlledCount}/${consignment.unassignedLoadCount}` 
          : '-',
        shipFrom: consignment?.shipFromLocationName || '-',
        waypointCount: consignment?.waypointCount || 0,
        deliverTo: consignment?.deliverToLocationName || '-',
        returnLocation: '-',
        customer: consignment?.customerName || '-',
        waypoints: consignment?.waypoints || [],
      }
    })
  }, [route.loadIds, loadMetadataById, consignmentByShipmentBookingId])

  const handleOpenContentsSidePanel = (consignment: UnassignedConsignment) => {
    setContentsConsignment(consignment)
    setContentsSidePanelOpen(true)
  }

  const handleCloseContentsSidePanel = () => {
    setContentsSidePanelOpen(false)
    setContentsConsignment(null)
  }

  const handleOpenServicesSidePanel = (consignment: UnassignedConsignment, waypoint: UnassignedWaypoint) => {
    setServicesConsignment(consignment)
    setServicesWaypoint(waypoint)
    setServicesSidePanelOpen(true)
  }

  const handleCloseServicesSidePanel = () => {
    setServicesSidePanelOpen(false)
    setServicesConsignment(null)
    setServicesWaypoint(null)
  }

  const handleLocationChange= (event: SelectChangeEvent<number | ''>) => {
    setSelectedLocationId(event.target.value as number | '')
  }

  const handleCheckboxChange = (loadId: number) => {
    setSelectedLoadIds((prev) => {
      const newSelected = prev.includes(loadId)
        ? prev.filter((id) => id !== loadId)
        : [...prev, loadId]

      if (!prev.includes(loadId)) {
        setServiceConfigs((configs) => ({
          ...configs,
          [loadId]: {
            loadId,
            actionTypeId: DestinationActionTypeId.Pickup,
            clearanceRequired: false,
            selectedServiceIds: [],
          },
        }))
      }

      return newSelected
    })
  }

  const handleToggleExpand = (loadId: number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(loadId)) {
        newSet.delete(loadId)
      } else {
        newSet.add(loadId)
      }
      return newSet
    })
  }

  const handleActionTypeChange = (loadId: number, actionTypeId: number) => {
    setServiceConfigs((configs) => ({
      ...configs,
      [loadId]: {
        ...configs[loadId],
        actionTypeId,
      },
    }))
  }

  const handleClearanceChange = (loadId: number, clearanceRequired: boolean) => {
    setServiceConfigs((configs) => ({
      ...configs,
      [loadId]: {
        ...configs[loadId],
        clearanceRequired,
      },
    }))
  }

  const handleServiceSelect = (loadId: number, serviceId: number) => {
    setServiceConfigs((configs) => {
      const current = configs[loadId]
      if (!current) return configs

      const newServiceIds = current.selectedServiceIds.includes(serviceId)
        ? current.selectedServiceIds.filter((id) => id !== serviceId)
        : [...current.selectedServiceIds, serviceId]

      return {
        ...configs,
        [loadId]: {
          ...current,
          selectedServiceIds: newServiceIds,
        },
      }
    })
  }

  const handleAddServiceFromDropdown = (loadId: number, event: SelectChangeEvent<number | ''>) => {
    const serviceId = event.target.value as number
    if (serviceId) {
      handleServiceSelect(loadId, serviceId)
    }
  }

  const formatAddress = (address: { address1?: string; address2?: string; city?: string; state?: string; postalCode?: string; country?: string } | undefined): string | undefined => {
    if (!address) return undefined
    const parts = [address.address1, address.address2, address.city, address.state, address.postalCode, address.country].filter(Boolean)
    return parts.length > 0 ? parts.join(', ') : undefined
  }

  const handleAddClick = () => {
    if (selectedLocationId && scheduledDate && selectedLoadIds.length > 0) {
      const selectedLocation = locations.find((loc) => loc.id === selectedLocationId)
      const loadActions = selectedLoadIds.map((loadId) => ({
        loadId,
        destinationActionTypeId: serviceConfigs[loadId]?.actionTypeId || DestinationActionTypeId.Pickup,
      }))

      // Build loadServices from serviceConfigs
      const loadServices: { loadId: number; serviceId: number }[] = []
      selectedLoadIds.forEach((loadId) => {
        const config = serviceConfigs[loadId]
        if (config?.selectedServiceIds) {
          config.selectedServiceIds.forEach((serviceId) => {
            loadServices.push({ loadId, serviceId })
          })
        }
      })

      onAdd(
        selectedLocationId,
        selectedLocation?.name,
        formatAddress(selectedLocation?.address),
        scheduledDate.toISOString(),
        loadActions,
        loadServices
      )
      handleClose()
    }
  }

  const handleClose = () => {
    setSelectedLocationId('')
    setScheduledDate(null)
    setSelectedLoadIds([])
    setExpandedRows(new Set())
    setServiceConfigs({})
    onClose()
  }

  const isAddDisabled = !selectedLocationId || !scheduledDate || selectedLoadIds.length === 0

  const getServiceName = (serviceId: number): string => {
    const service = services.find((s: { id: number; name: string }) => s.id === serviceId)
    return service?.name || `Service ${serviceId}`
  }

  // Filter services by enabled, customerSelectable, and load type
  const getAvailableServicesForLoad = (loadId: number) => {
    const metadata = loadMetadataById[loadId]
    const loadTypeId = metadata?.loadTypeId
    const config = serviceConfigs[loadId]

    return services.filter((service: Service) => {
      // Must be enabled and customer selectable
      if (!service.enabled || !service.customerSelectable) return false
      // Must not already be selected
      if (config?.selectedServiceIds?.includes(service.id)) return false
      // Must support the load type (if load types are defined)
      if (service.loadTypes && service.loadTypes.length > 0 && loadTypeId) {
        return service.loadTypes.some((lt) => lt.loadTypeId === loadTypeId)
      }
      return true
    })
  }

  // Format date for display - use shared utility for ISO 8601 format
  const formatDateDisplay = (dateString?: string): string => {
    if (!dateString) return '-'
    return dayjs(dateString).format('YYYY-MM-DD')
  }

  return (
    <SideModalWrapper
      open={open}
      onClose={handleClose}
      headerLabel={formatMessage(messages.addDestination)}
      withBottomBox={false}
      width="900px"
    >
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {formatMessage(messages.customerRequests)}
        </Typography>

        <Table size="small" sx={{ mb: 3, '& .MuiTableCell-root': { py: 1, px: 1 } }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ width: 40 }} />
              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                {formatMessage(messages.trackingNumber)}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                {formatMessage(messages.type)}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                {formatMessage(messages.quantity)}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                {formatMessage(messages.tempControlled)}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                {formatMessage(messages.shipFrom)}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                {formatMessage(messages.waypoints)}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                {formatMessage(messages.deliverTo)}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                {formatMessage(messages.returnLocation)}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                {formatMessage(messages.customer)}
              </TableCell>
              <TableCell padding="checkbox" />
            </TableRow>
          </TableHead>
          <TableBody>
            {customerRequestRows.map((row) => (
              <>
                <TableRow key={row.loadId}>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleToggleExpand(row.loadId)}>
                      {expandedRows.has(row.loadId) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{row.trackingNumber}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{row.type}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>
                    {consignmentByShipmentBookingId[row.shipmentBookingId] ? (
                      <Link
                        component="button"
                        variant="body2"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleOpenContentsSidePanel(consignmentByShipmentBookingId[row.shipmentBookingId])
                        }}
                        sx={{ cursor: 'pointer', fontSize: '0.8rem' }}
                      >
                        {row.quantity}
                      </Link>
                    ) : (
                      row.quantity
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{row.tempControlled}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{row.shipFrom}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{row.waypointCount}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{row.deliverTo}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{row.returnLocation}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{row.customer}</TableCell>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedLoadIds.includes(row.loadId)}
                      onChange={() => handleCheckboxChange(row.loadId)}
                    />
                  </TableCell>
                </TableRow>
                <TableRow key={`${row.loadId}-expanded`}>
                  <TableCell colSpan={11} sx={{ py: 0, borderBottom: expandedRows.has(row.loadId) ? undefined : 'none' }}>
                    <Collapse in={expandedRows.has(row.loadId)} timeout="auto" unmountOnExit>
                      <Box sx={{ py: 2, px: 2, bgcolor: 'grey.50' }}>
                        {row.waypoints.length > 0 ? (
                          [...row.waypoints]
                            .sort((a, b) => a.sequence - b.sequence)
                            .map((waypoint, wpIdx) => (
                              <Box key={waypoint.waypointId} sx={{ mb: wpIdx < row.waypoints.length - 1 ? 2 : 0 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                  {waypoint.waypointTypeName || `Waypoint ${wpIdx + 1}`}: {waypoint.locationName || '-'}
                                </Typography>
                                
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1, mb: 1 }}>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">Location</Typography>
                                    <Typography variant="body2">{waypoint.locationName || '-'}</Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">Contact</Typography>
                                    <Typography variant="body2">{waypoint.contactName || '-'}</Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">Special Instructions</Typography>
                                    <Typography variant="body2">{waypoint.instructions || '-'}</Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">Clearing Required</Typography>
                                    <Typography variant="body2">{waypoint.clearingRequired ? 'Yes' : 'No'}</Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">Services</Typography>
                                    {waypoint.services && consignmentByShipmentBookingId[row.shipmentBookingId] ? (
                                      <Link
                                        component="button"
                                        variant="body2"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          handleOpenServicesSidePanel(consignmentByShipmentBookingId[row.shipmentBookingId], waypoint)
                                        }}
                                        sx={{ cursor: 'pointer', display: 'block', textAlign: 'left' }}
                                      >
                                        {waypoint.services}
                                      </Link>
                                    ) : (
                                      <Typography variant="body2">{waypoint.services || '-'}</Typography>
                                    )}
                                  </Box>
                                </Box>

                                {waypoint.transitDetails && waypoint.transitDetails.length > 0 && (
                                  <>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
                                      Transit Details
                                    </Typography>
                                    {waypoint.transitDetails.map((transit, tIdx) => (
                                      <Box key={transit.transitDetailId} sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, mb: tIdx < waypoint.transitDetails!.length - 1 ? 1 : 0 }}>
                                        <Box>
                                          <Typography variant="caption" color="text.secondary">Mode of Transit</Typography>
                                          <Typography variant="body2">{transit.transportModeName || '-'}</Typography>
                                        </Box>
                                        <Box>
                                          <Typography variant="caption" color="text.secondary">Cross Border</Typography>
                                          <Typography variant="body2">{transit.isCrossBorder ? 'Yes' : 'No'}</Typography>
                                        </Box>
                                        <Box>
                                          <Typography variant="caption" color="text.secondary">Fulfilment Req.</Typography>
                                          <Typography variant="body2">{transit.fulfilmentRequired ? 'Yes' : 'No'}</Typography>
                                        </Box>
                                        <Box>
                                          <Typography variant="caption" color="text.secondary">Preferred Carrier</Typography>
                                          <Typography variant="body2">{transit.preferredCarrierName || '-'}</Typography>
                                        </Box>
                                        <Box>
                                          <Typography variant="caption" color="text.secondary">Pre-booked Transit ID</Typography>
                                          <Typography variant="body2">{transit.preBookedTransitIdentifier || '-'}</Typography>
                                        </Box>
                                        <Box>
                                          <Typography variant="caption" color="text.secondary">Sched. Departure</Typography>
                                          <Typography variant="body2">{formatDateDisplay(transit.scheduledDeparture)}</Typography>
                                        </Box>
                                        <Box>
                                          <Typography variant="caption" color="text.secondary">Sched. Arrival</Typography>
                                          <Typography variant="body2">{formatDateDisplay(transit.scheduledArrival)}</Typography>
                                        </Box>
                                      </Box>
                                    ))}
                                  </>
                                )}
                                
                                {wpIdx < row.waypoints.length - 1 && <Divider sx={{ mt: 2 }} />}
                              </Box>
                            ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No waypoint details available.
                          </Typography>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </>
            ))}
          </TableBody>
        </Table>

        <Typography variant="h6" sx={{ mb: 2 }}>
          {formatMessage(messages.selectDestination)}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 300 }}>
            <InputLabel id="destination-location-label">
              {formatMessage(messages.destination)}
            </InputLabel>
            <Select
              labelId="destination-location-label"
              value={selectedLocationId}
              label={formatMessage(messages.destination)}
              onChange={handleLocationChange}
            >
              <MenuItem value="">
                <em>{formatMessage(messages.selectLocation)}</em>
              </MenuItem>
              {locations.map((location) => (
                <MenuItem key={location.id} value={location.id}>
                  {location.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <DatePicker
            label={formatMessage(messages.scheduledDate)}
            value={scheduledDate}
            onChange={(newValue) => setScheduledDate(newValue)}
            minDate={minDate || dayjs().startOf('day')}
            format="YYYY-MM-DD"
            slotProps={{ textField: { size: 'small', sx: { minWidth: 180 } } }}
          />
        </Box>

        {selectedLoadIds.length > 0 && (
          <>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {formatMessage(messages.selectServices)}
            </Typography>

            <Table size="small" sx={{ mb: 3, '& .MuiTableCell-root': { py: 1, px: 1 } }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                    {formatMessage(messages.consignment)}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                    {formatMessage(messages.pickUpDropOff)}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                    {formatMessage(messages.clearance)}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                    {formatMessage(messages.service)}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                    {formatMessage(messages.selectedServices)}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedLoadIds.map((loadId) => {
                  const metadata = loadMetadataById[loadId]
                  const config = serviceConfigs[loadId]
                  return (
                    <TableRow key={loadId}>
                      <TableCell sx={{ fontSize: '0.8rem' }}>
                        {metadata?.trackingNumber || '-'}
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <Select
                            value={config?.actionTypeId || DestinationActionTypeId.Pickup}
                            onChange={(e) => handleActionTypeChange(loadId, e.target.value as number)}
                          >
                            <MenuItem value={DestinationActionTypeId.Pickup}>
                              {formatMessage(messages.pickup)}
                            </MenuItem>
                            <MenuItem value={DestinationActionTypeId.DropOff}>
                              {formatMessage(messages.dropoff)}
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleClearanceChange(loadId, !config?.clearanceRequired)}
                          sx={{ color: config?.clearanceRequired ? 'success.main' : 'error.main' }}
                        >
                          {config?.clearanceRequired ? <CheckIcon /> : <CloseIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                          <Select
                            value=""
                            displayEmpty
                            onChange={(e) => handleAddServiceFromDropdown(loadId, e)}
                          >
                            <MenuItem value="" disabled>
                              {formatMessage(messages.selectAService)}
                            </MenuItem>
                            {getAvailableServicesForLoad(loadId)
                              .map((service: { id: number; name: string }) => (
                                <MenuItem key={service.id} value={service.id}>
                                  {service.name}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {config?.selectedServiceIds.length > 0 ? (
                            config.selectedServiceIds.map((serviceId) => (
                              <Chip
                                key={serviceId}
                                label={getServiceName(serviceId)}
                                size="small"
                                onDelete={() => handleServiceSelect(loadId, serviceId)}
                              />
                            ))
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {formatMessage(messages.noServicesSelected)}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
          <Button variant="outlined" onClick={handleClose}>
            {formatMessage(messages.cancel)}
          </Button>
          <Button variant="contained" onClick={handleAddClick} disabled={isAddDisabled}>
            {formatMessage(messages.add)}
          </Button>
        </Box>
      </Box>
      <ConsignmentContentsSidePanel
        open={contentsSidePanelOpen}
        onClose={handleCloseContentsSidePanel}
        consignment={contentsConsignment}
        tenantId={tenantId}
      />
      <ConsignmentServicesSidePanel
        open={servicesSidePanelOpen}
        onClose={handleCloseServicesSidePanel}
        consignment={servicesConsignment}
        tenantId={tenantId}
        waypoint={servicesWaypoint}
      />
    </SideModalWrapper>
  )
}

export default AddDestinationSidePanel
